import axiosInstance from "@/axios";
import React, { useEffect, useState } from "react";
import { FaArrowRightLong, FaArrowLeftLong } from "react-icons/fa6";

const BlogSlider = () => {
    const [blogs, setBlogs] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Function to get random Unsplash image
    const getRandomImage = (blogId) => {
        const topics = [
            'business',
            'technology',
            'writing',
            'blog',
            'article',
            'news',
            'digital',
            'computer',
            'startup',
            'work',
            'office',
            'creative',
            'design',
            'content',
            'marketing'
        ];
        
        const randomTopic = topics[blogId % topics.length];
        const randomSeed = blogId || Math.floor(Math.random() * 1000);
        
        return `https://images.unsplash.com/photo-${1486312338219 + randomSeed}?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80&sig=${randomSeed}&topic=${randomTopic}`;
    };

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await axiosInstance.get("/blogs");
                setBlogs(response.data);
            } catch (error) {
                console.error("Error fetching blogs:", error);
            }
        };
        fetchBlogs();
    }, []);

    // Auto-slide functionality
    useEffect(() => {
        if (blogs.length <= 1 || isPaused || isTransitioning) return;

        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex(prevIndex => 
                    prevIndex === blogs.length - 1 ? 0 : prevIndex + 1
                );
                setIsTransitioning(false);
            }, 150);
        }, 5000); // 2 seconds

        return () => clearInterval(interval);
    }, [blogs.length, isPaused, isTransitioning]);

    const nextSlide = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        
        setTimeout(() => {
            if (currentIndex < blogs.length - 1) {
                setCurrentIndex(currentIndex + 1);
            } else {
                setCurrentIndex(0); // Loop back to first
            }
            setIsTransitioning(false);
        }, 150);
    };

    const prevSlide = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        
        setTimeout(() => {
            if (currentIndex > 0) {
                setCurrentIndex(currentIndex - 1);
            } else {
                setCurrentIndex(blogs.length - 1); // Loop to last
            }
            setIsTransitioning(false);
        }, 150);
    };

    const goToSlide = (index) => {
        if (isTransitioning || index === currentIndex) return;
        setIsTransitioning(true);
        
        setTimeout(() => {
            setCurrentIndex(index);
            setIsTransitioning(false);
        }, 150);
    };

    if (blogs.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center">
                    <div className="animate-pulse">
                        <div className="bg-gray-200 h-8 w-48 mx-auto mb-8 rounded"></div>
                        <div className="bg-gray-200 h-96 w-full rounded-lg"></div>
                    </div>
                </div>
            </div>
        );
    }

    const currentBlog = blogs[currentIndex];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">Latest Blogs</h2>

            {/* Main Blog Card */}
            <div className="relative overflow-hidden">
                <div 
                    className={`bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 ease-in-out ${
                        isTransitioning ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
                    }`}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className="md:flex">
                        {/* Image Section */}
                        <div className="md:w-1/2">
                            <div className="h-64 md:h-96 relative overflow-hidden">
                                <img
                                    src={currentBlog.featured_media_url || currentBlog.jetpack_featured_media_url || getRandomImage(currentBlog.id || currentIndex)}
                                    alt={currentBlog.title?.rendered || "Blog post"}
                                    className={`w-full h-full object-cover transition-all duration-500 ease-in-out ${
                                        isTransitioning ? 'transform scale-110 opacity-80' : 'transform scale-100 opacity-100 hover:scale-105'
                                    }`}
                                    onError={(e) => {
                                        // If the random image fails, try a different approach
                                        const fallbackImages = [
                                            "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
                                            "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
                                            "https://images.unsplash.com/photo-1542831371-29b0f74f9713?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
                                            "https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
                                            "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                                        ];
                                        const randomFallback = fallbackImages[(currentBlog.id || currentIndex) % fallbackImages.length];
                                        e.target.src = randomFallback;
                                    }}
                                />
                                <div className={`!absolute top-4 left-4 transition-all duration-300 ${
                                    isTransitioning ? 'opacity-0 transform -translate-x-4' : 'opacity-100 transform translate-x-0'
                                }`}>
                                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        {new Date(currentBlog.date).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                                {/* Auto-slide indicator */}
                                <div className={`!absolute top-4 right-4 transition-all duration-300 ${
                                    isTransitioning ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
                                }`}>
                                    <div className="bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs flex items-center space-x-1">
                                        <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-400' : 'bg-green-400'}`}></div>
                                        <span>{isPaused ? 'Paused' : 'Auto'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                            <div className={`space-y-6 transition-all duration-300 ease-in-out ${
                                isTransitioning ? 'opacity-0 transform translate-x-8' : 'opacity-100 transform translate-x-0'
                            }`}>
                                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                                    {currentBlog.title?.rendered}
                                </h3>
                                
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    {currentBlog.excerpt?.rendered
                                        ? currentBlog.excerpt.rendered
                                            .replace(/<\/?[^>]+(>|$)/g, "")
                                            .replace(/&[^;]+;/g, ' ')
                                            .trim()
                                            .substring(0, 250)
                                        : currentBlog.content?.rendered
                                            ? currentBlog.content.rendered
                                                .replace(/<\/?[^>]+(>|$)/g, "")
                                                .replace(/&[^;]+;/g, ' ')
                                                .trim()
                                                .substring(0, 250)
                                            : "Discover insights and updates from our latest blog post. Click to read the full article and explore more content."
                                    }{(currentBlog.excerpt?.rendered || currentBlog.content?.rendered) && "..."}
                                </p>

                                {/* Categories/Tags if available */}
                                {currentBlog.categories && currentBlog.categories.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {currentBlog.categories.slice(0, 3).map((category, idx) => (
                                            <span
                                                key={idx}
                                                className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium"
                                            >
                                                {category.name || category}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Author and Read Time if available */}
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                    {currentBlog.author && (
                                        <span className="flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                            </svg>
                                            {currentBlog.author.name || currentBlog.author}
                                        </span>
                                    )}
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                        2 min read
                                    </span>
                                </div>

                                <a
                                    href={currentBlog.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 group"
                                >
                                    <span>Read Full Article</span>
                                    <FaArrowRightLong className="transition-transform group-hover:translate-x-1" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-center items-center mt-8 space-x-6">
                <button 
                    onClick={prevSlide} 
                    className="p-3 rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={blogs.length <= 1 || isTransitioning}
                >
                    <FaArrowLeftLong className="text-gray-600 hover:text-blue-500 transition-colors" size={20} />
                </button>

                {/* Dots Indicator */}
                <div className="flex space-x-3">
                    {blogs.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-200 ${
                                currentIndex === index 
                                    ? "w-8 h-2 bg-blue-500 rounded-full" 
                                    : "w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400"
                            } ${isTransitioning ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isTransitioning}
                        />
                    ))}
                </div>

                <button 
                    onClick={nextSlide} 
                    className="p-3 rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={blogs.length <= 1 || isTransitioning}
                >
                    <FaArrowRightLong className="text-gray-600 hover:text-blue-500 transition-colors" size={20} />
                </button>
            </div>

            {/* Blog Counter */}
            <div className="text-center mt-4">
                <span className="text-gray-500 text-sm">
                    {currentIndex + 1} of {blogs.length}
                </span>
            </div>
        </div>
    );
};

export default BlogSlider;